import { useCallback, useId } from "react";
import { useShallow } from "zustand/react/shallow";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useAdminFormEditorPaths } from "../../hooks/use-admin-form-editor-paths";
import { useFormEditorStore } from "../../state/form-editor-store";
import type { PaletteFieldType } from "../../types";
import { FieldPropertiesPanel } from "../field-properties-panel";
import { FieldPalette } from "./form-builder-palette";
import { translatePaletteFieldTypeLabel } from "./form-builder-palette-label";

/**
 * Right column: field palette or properties for the selected node.
 * Subscribes only to selection and palette-add paths so the rail updates independently of deep canvas edits where possible.
 */
export function FormBuilderSidebar() {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();

	const { selectedId, selectedNode, setSelectedId, addFieldFromPalette } =
		useFormEditorStore(
			useShallow((s) => {
				const sid = s.selectedId;
				return {
					selectedId: sid,
					selectedNode: sid ? s.nodes[sid] : undefined,
					setSelectedId: s.setSelectedId,
					addFieldFromPalette: s.addFieldFromPalette,
				};
			}),
		);

	const translatePaletteTypeLabel = useCallback(
		(fieldType: PaletteFieldType) =>
			translatePaletteFieldTypeLabel(t, paths, fieldType),
		[t, paths],
	);

	const paletteTitleId = useId();
	const showProperties = selectedId !== null;

	return (
		<aside
			className="flex w-80 shrink-0 flex-col border-l border-border-secondary bg-background-primary"
			aria-labelledby={
				showProperties && selectedNode
					? "form-builder-properties-heading"
					: paletteTitleId
			}
		>
			{showProperties && selectedNode && selectedId ? (
				<FieldPropertiesPanel
					nodeId={selectedId}
					node={selectedNode}
					onClose={() => {
						setSelectedId(null);
					}}
				/>
			) : (
				<FieldPalette
					titleId={paletteTitleId}
					onAddClick={(fieldType) => {
						addFieldFromPalette(
							fieldType,
							{ parent: "root" },
							translatePaletteTypeLabel(fieldType),
						);
					}}
				/>
			)}
		</aside>
	);
}
